import React, { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { toast } from 'sonner';
import { Pencil, Plus } from 'lucide-react';
import LeiderschapLayout from '@/components/LeiderschapLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { leiderschapApi } from '../api';
import SectionHeader from '../components/SectionHeader';
import useTeamMembers from '../hooks/useTeamMembers';
import { ACTION_ITEM_PRIORITIES, ACTION_ITEM_STATUSES, findClassName, findLabel } from '../labels';
import type { ActionItem, ActionItemInput, ActionItemPriority, ActionItemStatus } from '../types';

interface FormState {
  title: string;
  description: string;
  assignee_user_id: string;
  due_date: string;
  status: ActionItemStatus;
  priority: ActionItemPriority;
}

const EMPTY_FORM: FormState = {
  title: '',
  description: '',
  assignee_user_id: '',
  due_date: '',
  status: 'open',
  priority: 'normaal',
};

export default function ActionItemsPage() {
  const { user, hasCapability } = useAuth();
  const canManage = hasCapability('leiderschap_manage');
  const teamMembers = useTeamMembers();

  const [items, setItems] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<ActionItemStatus | 'all'>('all');
  const [onlyMine, setOnlyMine] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ActionItem | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const fetchItems = async () => {
    try {
      const data = await leiderschapApi.listActionItems();
      setItems(data);
    } catch (error) {
      toast.error('Kon actiepunten niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const filteredItems = useMemo(
    () =>
      items
        .filter((item) => statusFilter === 'all' || item.status === statusFilter)
        .filter((item) => !onlyMine || item.assignee_user_id === user?.id)
        .sort((a, b) => (a.due_date || '9999').localeCompare(b.due_date || '9999')),
    [items, statusFilter, onlyMine, user?.id],
  );

  const canEdit = (item: ActionItem) => canManage || item.assignee_user_id === user?.id;

  const openCreateDialog = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEditDialog = (item: ActionItem) => {
    setEditingItem(item);
    setForm({
      title: item.title,
      description: item.description || '',
      assignee_user_id: item.assignee_user_id || '',
      due_date: item.due_date || '',
      status: item.status,
      priority: item.priority,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.title) return;
    const payload: ActionItemInput = {
      title: form.title,
      description: form.description || null,
      assignee_user_id: form.assignee_user_id || null,
      due_date: form.due_date || null,
      status: form.status,
      priority: form.priority,
    };
    try {
      if (editingItem) {
        await leiderschapApi.updateActionItem(editingItem.id, payload);
        toast.success('Actiepunt bijgewerkt');
      } else {
        await leiderschapApi.createActionItem(payload);
        toast.success('Actiepunt aangemaakt');
      }
      setDialogOpen(false);
      fetchItems();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    }
  };

  const assigneeName = (userId: string | null) => teamMembers.find((m) => m.id === userId)?.name || '-';

  return (
    <LeiderschapLayout>
      <SectionHeader
        title="Actiepunten"
        description="Gedeeld accountability-bord voor toegewezen taken."
        action={canManage && (
          <Button onClick={openCreateDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Nieuw actiepunt
          </Button>
        )}
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={statusFilter} onValueChange={(value: ActionItemStatus | 'all') => setStatusFilter(value)}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            {ACTION_ITEM_STATUSES.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <Checkbox checked={onlyMine} onCheckedChange={(checked: boolean) => setOnlyMine(checked === true)} />
          Alleen mijn actiepunten
        </label>
      </div>

      <Card>
        <CardContent className="p-0">
          {!loading && filteredItems.length === 0 && (
            <p className="p-6 text-sm text-slate-500">Geen actiepunten gevonden.</p>
          )}
          <div className="divide-y divide-slate-100">
            {filteredItems.map((item) => (
              <div key={item.id} className="flex items-start justify-between gap-4 p-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-slate-900">{item.title}</p>
                    <Badge className={findClassName(ACTION_ITEM_STATUSES, item.status)}>
                      {findLabel(ACTION_ITEM_STATUSES, item.status)}
                    </Badge>
                    <Badge className={findClassName(ACTION_ITEM_PRIORITIES, item.priority)}>
                      {findLabel(ACTION_ITEM_PRIORITIES, item.priority)}
                    </Badge>
                  </div>
                  {item.description && <p className="mt-1 text-sm text-slate-500">{item.description}</p>}
                  <p className="mt-1 text-xs text-slate-400">
                    {assigneeName(item.assignee_user_id)}
                    {item.due_date && ` · deadline ${format(parseISO(item.due_date), 'd MMM yyyy', { locale: nl })}`}
                  </p>
                </div>
                {canEdit(item) && (
                  <Button variant="ghost" size="icon" onClick={() => openEditDialog(item)} aria-label="Bewerken">
                    <Pencil className="h-4 w-4 text-slate-400" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Actiepunt bewerken' : 'Nieuw actiepunt'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="action-item-title">Titel</Label>
              <Input
                id="action-item-title"
                value={form.title}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="action-item-description">Omschrijving</Label>
              <Textarea
                id="action-item-description"
                value={form.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm({ ...form, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="action-item-assignee">Toegewezen aan</Label>
                <Select
                  value={form.assignee_user_id || undefined}
                  onValueChange={(value: string) => setForm({ ...form, assignee_user_id: value })}
                >
                  <SelectTrigger id="action-item-assignee"><SelectValue placeholder="Kies iemand" /></SelectTrigger>
                  <SelectContent>
                    {teamMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="action-item-due-date">Deadline</Label>
                <Input
                  id="action-item-due-date"
                  type="date"
                  value={form.due_date}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, due_date: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="action-item-status">Status</Label>
                <Select value={form.status} onValueChange={(value: ActionItemStatus) => setForm({ ...form, status: value })}>
                  <SelectTrigger id="action-item-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTION_ITEM_STATUSES.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="action-item-priority">Prioriteit</Label>
                <Select value={form.priority} onValueChange={(value: ActionItemPriority) => setForm({ ...form, priority: value })}>
                  <SelectTrigger id="action-item-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTION_ITEM_PRIORITIES.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuleren</Button>
              <Button type="submit">{editingItem ? 'Opslaan' : 'Aanmaken'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </LeiderschapLayout>
  );
}
